/**
 * LCFPIImpactTab.js — Impact Tab
 * Wraps: Carbon Monetisation, PoA Programme, PoA Deal Room,
 *        Investment Map (geo), Portfolio (climate lens)
 * Investment Map is the visual centrepiece of this tab.
 */
import React, { useState, Suspense, lazy } from "react";
import { DollarSign, GitMerge, Globe, BarChart2, Award } from "lucide-react";
import { LCFPIErrorBoundary } from "@/components/lcfpi/LCFPIPageShell";

const LCFPIMonetisation  = lazy(() => import("./LCFPIMonetisation"));
const LCFPIPOA           = lazy(() => import("./LCFPIPOA"));
const LCFPIPOADealRoom   = lazy(() => import("./LCFPIPOADealRoom"));
const LCFPIEcoLabels     = lazy(() => import("./LCFPIEcoLabels"));
// Investment Map — rendered directly for visual prominence
const LCFPIGeoMap        = lazy(() => import("@/components/lcfpi/LCFPIGeoMap"));

const SECTIONS = [
  { id: "map",         label: "Investment Map",        icon: Globe,     accent: "#16a34a", desc: "Geographic portfolio view" },
  { id: "monetisation",label: "Carbon Monetisation",   icon: DollarSign,accent: "#059669", desc: "Revenue · PoA · PDD"       },
  { id: "poa",         label: "PoA Programme",         icon: GitMerge,  accent: "#8b5cf6", desc: "Aggregation intelligence"  },
  { id: "poa-deal",    label: "PoA Deal Room",         icon: BarChart2, accent: "#0891b2", desc: "Structuring & co-finance"  },
  { id: "eco",         label: "Eco-Labels & SDGs",     icon: Award,     accent: "#d97706", desc: "Certification pathways"    },
];

const Loader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function LCFPIImpactTab() {
  const [active, setActive] = useState("map");
  const sec = SECTIONS.find(s => s.id === active) || SECTIONS[0];

  return (
    <div style={{ minHeight: "calc(100vh - 88px)", background: "#060d1a" }}>
      <div style={{ background: "#07101e", borderBottom: "1px solid #1e293b", padding: "0 24px", display: "flex", gap: 2, alignItems: "center", overflowX: "auto" }}>
        {SECTIONS.map(s => {
          const isActive = active === s.id; const Icon = s.icon;
          return (
            <button key={s.id} data-testid={`lcfpi-impact-sub-${s.id}`}
              onClick={() => setActive(s.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", background: isActive ? `${s.accent}18` : "transparent", border: "none", borderBottom: `2px solid ${isActive ? s.accent : "transparent"}`, color: isActive ? "#fff" : "#64748b", fontSize: 11, fontWeight: isActive ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all .15s" }}>
              <Icon size={11} color={isActive ? s.accent : "currentColor"} />
              {s.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid #0f172a", background: "#060d1a" }}>
        <p style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: ".08em" }}>
          <span style={{ color: sec.accent, fontWeight: 700 }}>Impact</span> › {sec.label} · {sec.desc}
        </p>
      </div>
      <LCFPIErrorBoundary>
        <Suspense fallback={<Loader />}>
          {active === "map" ? (
            /* Investment Map gets full height treatment */
            <div style={{ height: "calc(100vh - 130px)", padding: "0" }}>
              <LCFPIGeoMap />
            </div>
          ) : (
            <div style={{ padding: "20px 24px" }}>
              {active === "monetisation" && <LCFPIMonetisation />}
              {active === "poa"          && <LCFPIPOA />}
              {active === "poa-deal"     && <LCFPIPOADealRoom />}
              {active === "eco"          && <LCFPIEcoLabels />}
            </div>
          )}
        </Suspense>
      </LCFPIErrorBoundary>
    </div>
  );
}
